import { Component, Inject, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { email, FieldTree, form, required } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { TilintarkastajatKutsuUusiRequest, TilintarkastajatKutsuUusiResponse } from 'app/_jaettu-lemonator/model/tilintarkastaja'
import {
  TILINTARKASTAJA_KUTSU_VIESTIN_AIHE,
  TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_ALA,
  TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_TAIKALINKKI,
  TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_YLA,
  TilintarkastajaService,
} from 'app/_jaettu-lemonator/service/tilintarkastaja.service'

interface TilintarkastajaKutsuLomake {
  etunimi: string
  sukunimi: string
  sahkoposti: string
  kutsuAihe: string
  kutsuViestiYla: string
  kutsuViestiAla: string
}

export interface TilintarkastajaKutsuDialogData {
  asiakasAvain: string
  tilikausiAvain: string
}

@Component({
  selector: 'app-tilintarkastaja-kutsu-dialog',
  templateUrl: './tilintarkastaja-kutsu-dialog.component.html',
  styleUrls: ['./tilintarkastaja-kutsu-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TilintarkastajaKutsuDialogComponent implements OnInit {
  readonly TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_TAIKALINKKI = TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_TAIKALINKKI

  lomake = form(
    signal<TilintarkastajaKutsuLomake>({
      etunimi: '',
      sukunimi: '',
      sahkoposti: '',
      kutsuAihe: TILINTARKASTAJA_KUTSU_VIESTIN_AIHE,
      kutsuViestiYla: TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_YLA,
      kutsuViestiAla: TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_ALA,
    }),
    polku => {
      required(polku.etunimi)
      required(polku.sukunimi)
      required(polku.sahkoposti)
      email(polku.sahkoposti)
      required(polku.kutsuAihe)
      required(polku.kutsuViestiYla)
      required(polku.kutsuViestiAla)
    },
  )

  constructor(
    public dialogRef: MatDialogRef<TilintarkastajaKutsuDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TilintarkastajaKutsuDialogData,
    private _firebaseLemonator: FirebaseLemonator,
    private _tilintarkastajaService: TilintarkastajaService,
    private _snackbar: MatSnackBar,
    private _ladataanService: LadataanService,
  ) {}

  ngOnInit() {}

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  peruuta() {
    this.dialogRef.close(false)
  }

  async lahetaKutsu() {
    if (this.lomake().invalid()) {
      return
    }

    const payload = this.lomake().value()
    this._ladataanService.aloitaLataaminen()

    try {
      const kutsunViesti = this._tilintarkastajaService.annaTilintarkastajaKutsuViesti(payload.kutsuViestiYla, TILINTARKASTAJA_KUTSU_VIESTIN_SISALTO_TAIKALINKKI, payload.kutsuViestiAla || '')
      const tekstiWithBreaks = kutsunViesti.replace(/(?:\r\n|\r|\n)/g, '<br/>')

      const result = await this._firebaseLemonator.functionsCall<TilintarkastajatKutsuUusiRequest, TilintarkastajatKutsuUusiResponse>('tilintarkastajatKutsuUusi', {
        asiakasAvain: this.data.asiakasAvain,
        tilikausiAvain: this.data.tilikausiAvain,
        etunimi: payload.etunimi,
        sukunimi: payload.sukunimi,
        email: payload.sahkoposti,
        viestinAihe: payload.kutsuAihe,
        viestinSisalto: tekstiWithBreaks,
      })

      if (result.e) {
        let virhe: string
        switch (result.e) {
          case 'tilintarkastaja-exists':
            virhe = 'Tilintarkastaja samalla nimellä on jo olemassa'
            break
          case 'invalid-email':
            virhe = 'Virheellinen sähköpostiosoite'
            break
          case 'missing-data':
            virhe = 'Pakolliset tiedot puuttuvat'
            break
          default:
            virhe = 'Kutsun lähetys epäonnistui'
        }
        this._snackbar.open(virhe, null, { duration: 10000 })
        return
      }

      this._snackbar.open('Tilintarkastaja on lisätty järjestelmään ja kutsuttu sähköpostitse. Voit lisätä tilintarkastajan tilinpäätökselle.', null, { duration: 10000 })
      this.dialogRef.close(true) // Palauttaa true jos kutsu onnistui
    } catch (err) {
      const virhe = 'Kutsun lähetys epäonnistui: ' + (err instanceof Error ? err.message : 'Tuntematon virhe')
      this._snackbar.open(virhe, null, { duration: 10000 })
      console.error('Kutsun lähetys epäonnistui', err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }
}
